import React, { useState, useEffect, useRef } from 'react';
import { Challenge } from '../../types';
import { challengeAPI, containerAPI } from '../../services/api';
import { Terminal } from './Terminal';
import { 
  ArrowLeft, 
  Play, 
  CheckCircle, 
  AlertCircle, 
  Terminal as TerminalIcon,
  Clock,
  Award,
  Target,
  Zap,
  StopCircle,
  Timer,
  Tag,
  Terminal as TerminalIcon2
} from 'lucide-react';
import clsx from 'clsx';

interface ChallengeViewProps {
  challenge: Challenge;
  onBack: () => void;
}

export const ChallengeView: React.FC<ChallengeViewProps> = ({ challenge, onBack }) => {
  console.log('ChallengeView component rendered with challenge:', challenge); // DEBUG LOG
  
  const [containerId, setContainerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{ success: boolean; message: string; points_earned?: number } | null>(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [checkingActive, setCheckingActive] = useState(true);
  const [containerStatus, setContainerStatus] = useState<'none' | 'deploying' | 'active' | 'error' | 'running'>('none');
  const startLock = useRef(false);

  // Check for active containers on mount and periodically
  useEffect(() => {
    console.log('useEffect for polling active containers triggered'); // DEBUG LOG
    console.log('Current challenge ID:', challenge.id); // DEBUG LOG
    console.log('Current container status:', containerStatus); // DEBUG LOG
    
    const checkActiveContainers = async () => {
      console.log('checkActiveContainers function called'); // DEBUG LOG
      try {
        console.log('Making API request to /v1/user/active-containers'); // DEBUG LOG
        const response = await containerAPI.getActiveContainers();
        console.log('Active containers response:', JSON.stringify(response, null, 2)); // DEBUG LOG
        const containers = response.containers || [];
        const challengeContainer = containers.find(
          (c: any) => c.challenge_id === challenge.id
        );
        console.log('Current challenge ID:', challenge.id); // DEBUG LOG
        console.log('Found challenge container:', JSON.stringify(challengeContainer, null, 2)); // DEBUG LOG
        
        if (challengeContainer) {
          console.log('Setting container ID:', challengeContainer.container_id); // DEBUG LOG
          setContainerId(challengeContainer.container_id);
          
          const newStatus = challengeContainer.status === 'running' ? 'active' : challengeContainer.status;
          console.log('Setting container status to:', newStatus); // DEBUG LOG
          setContainerStatus(newStatus);
          
          if (challengeContainer.status === 'running') {
            console.log('Container is running, setting expires at:', challengeContainer.expires_at); // DEBUG LOG
            setExpiresAt(new Date(challengeContainer.expires_at));
            setTimerActive(true);
            // Calculate time elapsed based on created_at
            if (challengeContainer.created_at) {
              const startedAt = new Date(challengeContainer.created_at);
              const elapsed = Math.floor((Date.now() - startedAt.getTime()) / 1000);
              console.log('Setting time elapsed:', elapsed); // DEBUG LOG
              setTimeElapsed(elapsed);
            }
          }
        } else {
          console.log('No active container found, resetting state'); // DEBUG LOG
          setContainerStatus('none');
          setContainerId(null);
          setTimerActive(false);
          setExpiresAt(null);
        }
      } catch (error) {
        console.error('Error checking active containers:', error);
        setContainerStatus('error');
      } finally {
        setCheckingActive(false);
      }
    };

    checkActiveContainers();
    // Check every 5 seconds
    const interval = setInterval(checkActiveContainers, 5000);
    return () => clearInterval(interval);
  }, [challenge.id]);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timerActive) {
      interval = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerActive]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'text-green-400 bg-green-900/20 border-green-500/30';
      case 'Medium': return 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30';
      case 'Hard': return 'text-red-400 bg-red-900/20 border-red-500/30';
      default: return 'text-gray-400 bg-gray-900/20 border-gray-500/30';
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return <Target className="w-4 h-4" />;
      case 'Medium': return <Clock className="w-4 h-4" />;
      case 'Hard': return <Zap className="w-4 h-4" />;
      default: return <Target className="w-4 h-4" />;
    }
  };

  const startChallenge = async () => {
    if (startLock.current || loading || checkingActive || !!containerId || ['active', 'deploying', 'running'].includes(containerStatus)) {
      return;
    }
    startLock.current = true; // Lock immediately
    setLoading(true);
    setValidationResult(null);
    setContainerStatus('deploying');
    try {
      console.log('Calling API to start challenge:', challenge.id); // DEBUG LOG
      const response = await challengeAPI.startChallenge(challenge.id);
      console.log('Start challenge response:', response); // DEBUG LOG
      setContainerId(response.container_id);
      // The status will be updated by the polling effect
    } catch (error: any) {
      console.error('Failed to start challenge:', error);
      setContainerStatus('error');
      setValidationResult({ 
        success: false, 
        message: error.message || 'Failed to start challenge. Please try again.'
      });
    } finally {
      setLoading(false);
      startLock.current = false; // Release lock
    }
  };

  const validateSolution = async () => {
    if (!containerId) return;
    
    setValidating(true);
    try {
      const result = await challengeAPI.validateSolution(challenge.id, containerId);
      setValidationResult(result);
      if (result.success) {
        setTimerActive(false);
      }
    } catch (error: any) {
      console.error('Validation failed:', error);
      setValidationResult({
        success: false,
        message: error.response?.data?.detail || 'Validation failed. Please try again.'
      });
    } finally {
      setValidating(false);
    }
  };

  const stopContainer = async () => {
    if (!containerId) return;
    
    try {
      await containerAPI.stopContainer(containerId);
      setContainerId(null);
      setTimerActive(false);
      setValidationResult(null);
      setExpiresAt(null);
    } catch (error) {
      console.error('Failed to stop container:', error);
    }
  };

  const timeRemaining = expiresAt ? Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000)) : 0;

  const isStartDisabled = loading || checkingActive || !!containerId || ['active', 'deploying', 'running'].includes(containerStatus);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* <Navbar /> */}
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors self-start"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>
          
          <div className="flex flex-col sm:flex-row gap-4">
            {timerActive && (
              <div className="flex items-center space-x-2 bg-gray-800/50 px-4 py-2 rounded-lg">
                <Clock className="w-4 h-4 text-blue-400" />
                <span className="text-white font-mono">{formatTime(timeElapsed)}</span>
              </div>
            )}
            
            {timeRemaining > 0 && containerId && (
              <div className="flex items-center space-x-2 bg-orange-900/50 px-4 py-2 rounded-lg">
                <Timer className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 font-mono">
                  Expires in {formatTime(timeRemaining)}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Challenge Info */}
          <div className="space-y-6">
            <div className="card p-6">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6 gap-4">
                <h1 className="text-2xl md:text-3xl font-bold text-white">{challenge.title}</h1>
                <span className={clsx(
                  "flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium border self-start",
                  getDifficultyColor(challenge.difficulty)
                )}>
                  {getDifficultyIcon(challenge.difficulty)}
                  <span>{challenge.difficulty}</span>
                </span>
              </div>
              
              <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-400">
                <div className="flex items-center space-x-1">
                  <Award className="w-4 h-4" />
                  <span>{challenge.points} points</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Timer className="w-4 h-4" />
                  <span>{challenge.time_limit_minutes} min limit</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Target className="w-4 h-4" />
                  <span>Max {challenge.max_attempts} attempts</span>
                </div>
              </div>

              {/* Tags */}
              {challenge.tags && challenge.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {challenge.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center space-x-1 px-2 py-1 bg-gray-700/50 text-gray-300 text-xs rounded-full"
                    >
                      <Tag className="w-3 h-3" />
                      <span>{tag}</span>
                    </span>
                  ))}
                </div>
              )}
              
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-300 leading-relaxed">
                  {challenge.description}
                </p>
              </div>

              {/* Challenge Status */}
              <div className="mt-6 space-y-4">
                {checkingActive ? (
                  <div className="flex items-center justify-center p-4">
                    <div className="loading-spinner h-6 w-6 mr-2"></div>
                    <span className="text-gray-300">Checking active challenges...</span>
                  </div>
                ) : containerStatus === 'deploying' ? (
                  <div className="flex items-center justify-center p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                    <div className="loading-spinner h-6 w-6 mr-2"></div>
                    <span className="text-blue-300">Deploying environment...</span>
                  </div>
                ) : containerStatus === 'error' ? (
                  <div className="flex items-center justify-center p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
                    <AlertCircle className="w-5 h-5 mr-2 text-red-400" />
                    <span className="text-red-300">Failed to deploy environment. Please try again.</span>
                  </div>
                ) : (containerStatus === 'active' || containerId) ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-green-400 bg-green-900/20 p-4 rounded-lg border border-green-500/30">
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                      <span>
                        {containerStatus === 'active'
                          ? 'Environment is ready! Use the terminal to solve the challenge.'
                          : 'You have an existing attempt. Reconnect to your environment.'}
                      </span>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => {
                          console.log('Connect to Console clicked, containerId:', containerId); // DEBUG LOG
                          const terminalSection = document.getElementById('terminal-section');
                          if (terminalSection) {
                            terminalSection.scrollIntoView({ behavior: 'smooth' });
                            const terminalInput = terminalSection.querySelector('input');
                            if (terminalInput) {
                              terminalInput.focus();
                            }
                          }
                        }}
                        className="btn-primary flex-1"
                      >
                        <span className="flex items-center justify-center">
                          <TerminalIcon2 className="w-4 h-4 mr-2" />
                          {containerStatus === 'active' ? 'Connect to Console' : 'Reconnect to Console'}
                        </span>
                      </button>
                      <button
                        onClick={validateSolution}
                        disabled={validating}
                        className="btn-secondary flex-1"
                      >
                        {validating ? (
                          <span className="flex items-center justify-center">
                            <div className="loading-spinner h-4 w-4 mr-2"></div>
                            Validating...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Check Solution
                          </span>
                        )}
                      </button>
                      <button
                        onClick={stopContainer}
                        className="btn-danger flex items-center justify-center px-6"
                      >
                        <StopCircle className="w-4 h-4 mr-2" />
                        <span className="hidden sm:inline">Stop</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      if (isStartDisabled) return;
                      startChallenge();
                    }}
                    disabled={isStartDisabled}
                    className={clsx(
                      "btn-primary w-full",
                      isStartDisabled && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <div className="loading-spinner h-4 w-4 mr-2"></div>
                        Deploying environment...
                      </span>
                    ) : !!containerId || ['active', 'deploying', 'running'].includes(containerStatus) ? (
                      <span className="flex items-center justify-center">
                        <Play className="w-4 h-4 mr-2" />
                        Challenge Already Active
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <Play className="w-4 h-4 mr-2" />
                        Start Challenge
                      </span>
                    )}
                  </button>
                )}

                {/* Validation Result */}
                {validationResult && (
                  <div className={clsx(
                    "p-4 rounded-lg border",
                    validationResult.success
                      ? 'bg-green-900/20 border-green-500/30 text-green-300'
                      : 'bg-red-900/20 border-red-500/30 text-red-300'
                  )}>
                    <div className="flex items-start space-x-2">
                      {validationResult.success ? (
                        <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className="font-medium">{validationResult.message}</p>
                        {validationResult.success && validationResult.points_earned && (
                          <p className="text-sm mt-1">
                            Points earned: {validationResult.points_earned}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tips section */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <TerminalIcon className="w-5 h-5 mr-2" />
                Getting Started
              </h3>
              <div className="space-y-3 text-sm text-gray-300">
                <div className="flex items-start space-x-3">
                  <span className="text-blue-400 font-mono font-bold">1.</span>
                  <span>Click "Start Challenge" to create your isolated environment</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-blue-400 font-mono font-bold">2.</span>
                  <span>Use the terminal to explore and diagnose the issue</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-blue-400 font-mono font-bold">3.</span>
                  <span>Common commands: <code className="bg-gray-700 px-1 rounded">ls</code>, <code className="bg-gray-700 px-1 rounded">ps aux</code>, <code className="bg-gray-700 px-1 rounded">systemctl status</code></span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-blue-400 font-mono font-bold">4.</span>
                  <span>Click "Check Solution" when you think you've fixed the issue</span>
                </div>
              </div>
            </div>
          </div>

          {/* Terminal */}
          <div className="space-y-6" id="terminal-section">
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <TerminalIcon className="w-5 h-5 mr-2" />
                Challenge Terminal
              </h3>
              <Terminal containerId={containerId} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
